// Agentic deck build: routes the approved plan from the create-new-slides
// dialog through the unified chat agent (apply_edits / get_slides_info) instead
// of the deterministic useDeckCreation pipeline. FactSet-only.

import { CHAT_CONTEXT_WINDOW_SIZE } from "../../shared/config/constants";
import { isGoldenArtifactCaptureEnabled } from "../config";
import {
  exportPresentationAsBase64Compressed,
  exportSlideAsImage,
  extractPresentationContent,
  extractWindowedPresentationContent,
  listSlides,
  parsePptxBase64AsReference,
} from "../services/slideService";
import { computeContextSlideWindow } from "../utils/contextWindow";
import { isPowerPointNativeAgentActive, queryOpenAIStreaming } from "../services/ai";
import { emptyTimeline, reduceAgentEvent } from "../services/ai/agentStream";
import type { PptxLangfuseRunContext } from "../langfuseTrace";
import { getReasoningNarrationConfig } from "../services/ai/narratorConfig";
import { createNarratingAgentEventSink } from "../services/ai/reasoningNarrator";
import {
  type ToolExecutionPolicy,
  type AgentToolContext,
  type ReferenceDeckExamples,
} from "../services/ai/types";
import { type DeckPlan } from "../services/ai/deckPlannerService";
import { applySkillRunContextToRuntime } from "../services/ai/skills";
import { buildSkillPreludeMessages } from "../services/ai/promptCatalog";
import {
  renderPlanningEvidencePack,
  unionGroundingCitations,
} from "../services/ai/planningEvidence";
import type {
  AgentProgressEvent,
  CreationProgress,
  StyleConfig,
} from "../components/DeckCreator/DeckCreator";
import { BRANDS, type BrandConfig, type BrandId } from "../services/ai/brandRegistry";
import { resolveSnippetPolicy } from "../services/ai/snippetPolicy";
import {
  activatePresentationViaIntents,
  extractDeckLayoutCatalogViaIntents,
  getSelectedSlideRefViaIntents,
  RUN_OFFICE_EXTRACTOR_TEXT_ONLY_SHAPE_FILTER,
  type FactSetPresentationRef,
} from "../services/factset/intentBridgeService";
import { deleteSlideById } from "../pptxSlideUtils";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import { isPowerPointActiveGraphsEnabled } from "../services/featureFlagService";
import { resolveLibrarySlidePptxSources } from "../services/ai/slideLibraryAgentTools";
import type { SlideLibrarySlide } from "../slideLibrary/be/types";
import type {
  DeckLayoutCatalog,
  PresentationModification,
  SlideCitationBinding,
  SlideRef,
  TabsState,
  ToolResult,
} from "../types";
import { buildSlideCitationMessages } from "./useQueryHandler";
import {
  buildBaseTemplateExamples,
  buildCuratedReferenceExamples,
  buildReferenceDeckExamples,
} from "./referenceDeckPrompt";
import { attachSyntheticMessageToSlideTab } from "../utils/chatMessages";
import {
  createGoldenArtifactRun,
  uploadGoldenArtifactJson,
  uploadGoldenArtifactPptx,
  type GoldenArtifactRun,
} from "../services/goldenArtifactsService";
import {
  createToolArtifactSubdirectoryAllocator,
  runGoldenCaptureStep,
} from "../utils/goldenCapture";
import { mergeIntoPool, type Citation } from "../citations";

// ---------------------------------------------------------------------------
// Post-build per-slide visual polish
//
// Runs after the user-visible deck build completes: replay a scoped chat-mode
// "fix obvious visual defects" message on each created slide, in bounded
// parallel, without adding chat transcript output.
// ---------------------------------------------------------------------------

/** Exact user message replayed (silently) per created slide. */
export const POST_BUILD_POLISH_QUERY = "only fix obvious visual defects if they exist";

const POST_BUILD_POLISH_ENABLED_KEY = "finster_post_build_polish_enabled";
const POST_BUILD_POLISH_CONCURRENCY_KEY = "finster_post_build_polish_concurrency";
const POST_BUILD_POLISH_CONCURRENCY_DEFAULT = 3;
const POST_BUILD_POLISH_CONCURRENCY_MIN = 1;
const POST_BUILD_POLISH_CONCURRENCY_MAX = 6;

export function collectPlanLibraryPptxSources(input: {
  librarySlidePptxUrl?: string;
  librarySlidePptxUrlCandidates?: string[];
  rememberedLibrarySlide?: {
    pptxUrl?: string;
    pptxUrlCandidates?: string[];
  };
}): string[] {
  return Array.from(
    new Set(
      [
        ...(input.librarySlidePptxUrlCandidates ?? []),
        input.librarySlidePptxUrl,
        ...(input.rememberedLibrarySlide?.pptxUrlCandidates ?? []),
        input.rememberedLibrarySlide?.pptxUrl,
      ]
        .map((source) => source?.trim())
        .filter((source): source is string => Boolean(source))
    )
  );
}

/** Post-build polish defaults ON; set localStorage flag to "false" to disable. */
export function isPostBuildVisualPolishEnabled(): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(POST_BUILD_POLISH_ENABLED_KEY);
      if (raw === "false") return false;
      if (raw === "true") return true;
    }
  } catch {
    // localStorage unavailable (non-browser context); fall through to default.
  }
  return true;
}

/** Bounded per-slide concurrency (default 3, tunable via localStorage, clamped 1..6). */
export function getPostBuildPolishConcurrency(): number {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(POST_BUILD_POLISH_CONCURRENCY_KEY);
      if (raw) {
        const parsed = Number.parseInt(raw, 10);
        if (Number.isFinite(parsed)) {
          return Math.min(
            POST_BUILD_POLISH_CONCURRENCY_MAX,
            Math.max(POST_BUILD_POLISH_CONCURRENCY_MIN, parsed)
          );
        }
      }
    }
  } catch {
    // localStorage unavailable; fall through to default.
  }
  return POST_BUILD_POLISH_CONCURRENCY_DEFAULT;
}

/**
 * Run `worker` over `items` with at most `limit` concurrent executions. The pool
 * keeps running after a worker rejects (per-item errors are the worker's
 * responsibility) and stops launching new work once `signal` aborts.
 */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
  options?: { signal?: AbortSignal }
): Promise<void> {
  if (items.length === 0) return;
  const effectiveLimit = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;
  const runNext = async (): Promise<void> => {
    for (;;) {
      if (options?.signal?.aborted) return;
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      try {
        await worker(items[index], index);
      } catch {
        // Worker handles its own failures; swallow to keep the pool alive.
      }
    }
  };
  await Promise.all(Array.from({ length: effectiveLimit }, () => runNext()));
}

/** A created slide selected for the polish pass, with its UI label metadata. */
export interface PostBuildPolishSlide {
  /** Real host slide id (stable targeting). */
  id: string;
  /** Current deck index. */
  index: number;
  /** Displayed host slide number, when known. */
  slideNumber?: number;
  /** 1-based position aligned with the per-slide UI rows (plan order). */
  planPosition: number;
  /** Plan title for the matching position (label only). */
  title: string;
}

/**
 * Identify slides created during THIS build, robustly (independent of the
 * partial-apply-gated `builtSlideIds` sink): primary signal is a build-start vs
 * build-end deck diff (`initialDeckSlideIds`), unioned with `builtSlideIds`.
 * Bootstrap/template seeds are excluded and the live `current` list naturally
 * drops any create-then-deleted slides. Result is sorted by deck index and
 * paired to plan order for labels only.
 */
export function identifyCreatedSlidesForPolish(params: {
  current: SlideRef[];
  initialDeckSlideIds: Set<string>;
  builtSlideIds: Set<string>;
  bootstrapSlideIds: Set<string>;
  planSlides: ReadonlyArray<{ title: string }>;
}): PostBuildPolishSlide[] {
  const { current, initialDeckSlideIds, builtSlideIds, bootstrapSlideIds, planSlides } = params;
  return current
    .filter(
      (slide) =>
        (!initialDeckSlideIds.has(slide.id) || builtSlideIds.has(slide.id)) &&
        !bootstrapSlideIds.has(slide.id)
    )
    .sort((a, b) => a.index - b.index)
    .map((slide, i) => ({
      id: slide.id,
      index: slide.index,
      ...(typeof slide.slideNumber === "number" ? { slideNumber: slide.slideNumber } : {}),
      planPosition: i + 1,
      title: planSlides[i]?.title ?? `Slide ${i + 1}`,
    }));
}

/**
 * Run the silent per-slide polish agent on ONE created slide: build a scoped,
 * id-targeted context (the same windowed context a chat message on that slide
 * would use), then drive the unified agent with the exact polish query. Edits
 * land in-loop via apply_edits; resolves on the agent's terminal payload and
 * rejects on agent error so the orchestrator can mark the slide.
 */
export async function runSlideVisualPolish(params: {
  slide: PostBuildPolishSlide;
  deckSlides: SlideRef[];
  presentationRef: FactSetPresentationRef | null;
  slideBounds: { slideWidth: number; slideHeight: number } | null;
  signal: AbortSignal;
  toolPolicy: ToolExecutionPolicy;
  log: (message: string) => void;
  /** Active golden-artifact run (null when capture is disabled). */
  goldenArtifactRun?: GoldenArtifactRun | null;
  /** Per-slide subdirectory namespace for golden capture (e.g. `polish_s01`). */
  goldenSubdirectoryPrefix?: string;
  /** Per-run Langfuse context so polish hops join the deck-build trace. */
  langfuseContext?: PptxLangfuseRunContext;
}): Promise<void> {
  const {
    slide,
    deckSlides,
    presentationRef,
    slideBounds,
    signal,
    toolPolicy,
    log,
    goldenArtifactRun,
    goldenSubdirectoryPrefix,
    langfuseContext,
  } = params;

  // Id-based window: compute neighbor indices, map to stable ids from the
  // snapshot, and scope the extract by ids so a concurrent structural change in
  // a sibling run cannot mis-target this run.
  const sorted = [...deckSlides].sort((a, b) => a.index - b.index);
  const { start, end } = computeContextSlideWindow(slide.index, sorted.length, CHAT_CONTEXT_WINDOW_SIZE);
  const windowIds = sorted
    .filter((s) => s.index >= start && s.index <= end)
    .map((s) => s.id);
  const scopedIds = windowIds.length > 0 ? windowIds : [slide.id];

  const content = await extractPresentationContent(log, {
    forceRefresh: false,
    presentationRef: presentationRef ?? undefined,
    filters: { slides: { ids: scopedIds } },
  });

  // The extractor resolves selectedSlideIndex to a window array position; override
  // it to the target's real deck index (match by stable id).
  const match = content.slides.find((s) => s.id === slide.id);
  if (match && typeof match.index === "number") {
    content.selectedSlideIndex = match.index;
  }
  content.totalDeckSlides = sorted.length;

  // Best-effort layout catalog so the agent can fill/insert like a chat turn.
  try {
    content.deckLayoutCatalog = await extractDeckLayoutCatalogViaIntents(log, {
      forceRefresh: false,
      presentationRef: presentationRef ?? undefined,
    });
  } catch {
    // Catalog is optional for a fix pass.
  }

  const slideImage = await exportSlideAsImage(match?.index ?? slide.index, {
    presentationRef: presentationRef ?? undefined,
    targetSlideId: slide.id,
  }).catch(() => "");

  // Golden capture (when enabled): give this run its OWN prefixed allocator so
  // per-tool artifacts land under collision-free, slide-scoped flat folders
  // (e.g. polish_s01_apply_00), and route this run's tool_trace into its own
  // subdirectory so it never overwrites the build's top-level trace.
  const agentToolContext: AgentToolContext = {
    presentationRef: presentationRef ?? null,
    slideBounds,
    signal,
    log,
    ...(goldenArtifactRun
      ? {
          goldenArtifactRun,
          nextGoldenToolSubdirectory: createToolArtifactSubdirectoryAllocator(
            goldenSubdirectoryPrefix
          ),
          ...(goldenSubdirectoryPrefix
            ? { goldenToolTraceSubdirectory: goldenSubdirectoryPrefix }
            : undefined),
        }
      : undefined),
  };

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const succeed = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const fail = (message: string): void => {
      if (settled) return;
      settled = true;
      reject(new Error(message));
    };
    queryOpenAIStreaming(
      content,
      POST_BUILD_POLISH_QUERY,
      {
        onThinking: () => {},
        onToolCall: () => {},
        onComplete: () => succeed(),
        onError: (error) => fail(error),
      },
      slideImage || undefined,
      [],
      toolPolicy,
      { signal, agentToolContext, langfuseContext, langfusePhase: "polish" }
    ).catch((error) => fail(error instanceof Error ? error.message : String(error)));
  });
}

function getAgentTerminalSummary(payload: PresentationModification): string | undefined {
  const reply = typeof payload.reply === "string" ? payload.reply.trim() : "";
  if (reply) return reply;
  const summary = typeof payload.summary === "string" ? payload.summary.trim() : "";
  return summary || undefined;
}

export function buildDeckBuildSkillRuntime(
  plan: DeckPlan,
  baseToolPolicy: ToolExecutionPolicy
): {
  buildToolPolicy: ToolExecutionPolicy;
  buildAgentToolContext: AgentToolContext;
  preludeUserMessages?: readonly string[];
  polishToolPolicy: ToolExecutionPolicy;
  /** Set when the shared Skill policy refused the run; the build must stop. */
  blockedMessage?: string;
  /** Targets the shared policy withheld; zero on every ordinary build. */
  omittedTargetCount?: number;
} {
  const polishToolPolicy = { ...baseToolPolicy };
  const buildToolPolicy = { ...baseToolPolicy };
  const buildAgentToolContext: AgentToolContext = {};
  if (!plan.skillRunContext) {
    return {
      buildToolPolicy,
      buildAgentToolContext,
      polishToolPolicy,
    };
  }
  const scope = applySkillRunContextToRuntime(
    plan.skillRunContext,
    buildToolPolicy,
    buildAgentToolContext
  );
  if (scope.status === "blocked") {
    return {
      buildToolPolicy,
      buildAgentToolContext,
      polishToolPolicy,
      blockedMessage: scope.message,
    };
  }
  return {
    buildToolPolicy,
    buildAgentToolContext,
    preludeUserMessages: buildSkillPreludeMessages(plan.skillRunContext.guidance, {
      targetFilesAttached: scope.omittedTargetCount === 0,
    }),
    polishToolPolicy,
    omittedTargetCount: scope.omittedTargetCount,
  };
}

/**
 * Pure: build the minimal seed prompt (the deck-build "user message"). The
 * durable "how to build a deck" guidance lives in the always-on operating
 * manual (Deck build loop / Base-slide selection / readability + final review);
 * the seed carries only the task-specific inputs: the task, the insert anchor,
 * the per-slide base-slide selections, the grounding context, and the plan JSON.
 */
export function buildDeckBuildSeedPrompt(
  plan: DeckPlan,
  opts: {
    anchorSlideId?: string | null;
    insertAfterCurrent?: boolean;
    styleDescription?: string | null;
    /** True when brand reference examples are attached via options.referenceExamples. */
    hasReferenceExamples?: boolean;
    /** True when base-slide templates (shape JSON + image) are attached via options.referenceExamples. */
    hasBaseSlideTemplates?: boolean;
    /**
     * Per-plan-index real slide id for slides whose plan entry selected an
     * existing-deck base slide (resolved from baseSlideIndex).
     */
    baseSlideIdByPlanIndex?: Record<number, string>;
  }
): string {
  const lines: string[] = [];
  lines.push(
    `Build this APPROVED ${plan.slides.length}-slide deck with apply_edits, following the Deck build loop in your operating manual. Honor the plan's slide COUNT (${plan.slides.length}) and ORDER exactly.`
  );
  if (opts.anchorSlideId) {
    lines.push(
      `Insert the first slide AFTER the slide with id "${opts.anchorSlideId}", then anchor each subsequent slide on the one you just created.`
    );
  } else {
    lines.push(
      `Insert slides at the ${
        opts.insertAfterCurrent === false ? "end of the deck" : "current position"
      } and build them in plan order.`
    );
  }

  const styleDescription = opts.styleDescription?.trim();
  if (styleDescription) {
    lines.push(`Style guidance: ${styleDescription}`);
  }
  if (opts.hasReferenceExamples) {
    lines.push(
      "Brand reference examples (a rendered image + its shape spec per slide) are attached as the visual standard - match their typography, palette, and shape language. Do not copy unused canvas from a sample page."
    );
  }

  // Per-slide base-slide selections (dynamic ids). The duplicate/insert +
  // fill-in-place mechanics live in the operating manual's "Base-slide
  // selection" section, so the seed only lists which slides reuse which base.
  const baseLines: string[] = [];
  let hasAnyBase = false;
  plan.slides.forEach((slide, i) => {
    const librarySlideId = (slide as { librarySlideId?: string }).librarySlideId;
    const baseSlideId = opts.baseSlideIdByPlanIndex?.[i];
    if (librarySlideId) {
      baseLines.push(`- Slide ${i + 1} "${slide.title}": LIBRARY library_slide_id "${librarySlideId}"`);
      hasAnyBase = true;
    } else if (baseSlideId) {
      baseLines.push(`- Slide ${i + 1} "${slide.title}": DUPLICATE base slide id "${baseSlideId}"`);
      hasAnyBase = true;
    } else {
      baseLines.push(`- Slide ${i + 1} "${slide.title}": FREEFORM - no base slide`);
    }
  });
  // Only surface the section when at least one slide reuses a base. A fully
  // freeform deck has no attached templates to disambiguate from, so an
  // all-FREEFORM list under a "BASE SLIDES" header would just be noise.
  if (hasAnyBase) {
    lines.push(
      `SELECTED BASE SLIDES (every slide is listed; honor each exactly per "Base-slide selection" in your manual):`
    );
    lines.push(...baseLines);
    lines.push(
      "Build the slides one at a time in plan order; for each base slide, duplicate/insert ONE per apply_edits call, then anchor the next slide on the id returned by the previous call (never batch duplicates)."
    );
    if (opts.hasBaseSlideTemplates) {
      lines.push(
        "Each selected base slide's shape spec (JSON) + rendered image are attached below - match their layout, structure, and styling; change the content to the slide's plan (clone repeating elements to fit more content)."
      );
      lines.push(
        "Each attached base slide applies ONLY to its assigned slide(s) listed above; do not use it as a template for FREEFORM slides - design those from their own content (you may still match the deck's palette, typography, and title placement for consistency)."
      );
    }
  }

  // Grounding facts (task-output decks): the model MUST ground slide content in
  // these and declare cited values; do NOT fabricate figures.
  const grounding = plan.groundingContextForSlides?.trim();
  if (grounding) {
    const groundingMessage = plan.groundingMessage?.trim();
    lines.push("");
    if (groundingMessage) {
      lines.push(`Grounding source: ${groundingMessage}`);
    }
    lines.push(
      "GROUNDING CONTEXT (your primary source for every fact; declare citations per your manual):"
    );
    lines.push(grounding);
  }

  const planningEvidence = plan.skillRunContext
    ? renderPlanningEvidencePack(plan.planningEvidence)
    : undefined;
  if (planningEvidence) {
    lines.push("");
    lines.push(planningEvidence);
  }

  const sanitizedSlides = plan.slides.map((slide) => {
    const librarySlideId = (slide as { librarySlideId?: string }).librarySlideId;
    return {
      index: slide.index,
      title: slide.title,
      bullets: slide.bullets,
      ...(librarySlideId ? { librarySlideId } : {}),
    };
  });
  lines.push("");
  lines.push(
    "Approved plan (JSON) - title is the argument; bullets are the evidence floor. GROUNDING CONTEXT is the fill list: if that slide's grounding has a table, metric grid, named roster, or dated list, those rows belong on the slide. If leftover grounded rows remain and unused body remains, add them as extra table rows or a sibling region. Do not stop after illustrating the plan bullets. FREEFORM / from-scratch slides: occupy 100% of the usable body band with grounded content (extend the last region; do not stretch 2–3 items). LIBRARY / DUPLICATE: fill remaining body; do not invent a coverage percentage:"
  );
  lines.push("```json");
  lines.push(JSON.stringify({ topic: plan.topic, slides: sanitizedSlides }, null, 2));
  lines.push("```");
  return lines.join("\n");
}

// --- Brand template bootstrap helpers (FactSet-only) ---

/**
 * Content fingerprint of a slide: shape count + its sorted visible text. Used to
 * distinguish an UNTOUCHED brand-template placeholder from one the agent filled.
 * The agent fills by adding shapes and/or text, so a filled slide's fingerprint
 * always differs from its original placeholder fingerprint, regardless of
 * whether the placeholder itself carried text (e.g. a divider).
 */
export function slideContentFingerprint(slide: { shapes?: Array<{ text?: string | null }> }): string {
  const texts = (slide.shapes || [])
    .map((shape) => (shape.text || "").trim())
    .filter((text) => text.length > 0)
    .sort();
  return `${(slide.shapes || []).length}|${texts.join("\u241F")}`;
}

/** Cap + render height for the brand reference-slide images attached to the seed turn. */
const REFERENCE_DECK_IMAGE_MAX = 12;
const REFERENCE_DECK_IMAGE_HEIGHT = 1080;

/**
 * Best-effort fetch of a remote image URL to raw base64 (no data: prefix), used
 * for the library-slide thumbnail fallback when the pptx capture fails. Returns
 * undefined on any failure - grounding for that slide is simply skipped.
 */
async function fetchImageUrlAsBase64(
  url: string,
  log: (message: string) => void
): Promise<string | undefined> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return undefined;
    const bytes = new Uint8Array(await resp.arrayBuffer());
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  } catch (err) {
    log(
      `[agentic-deck] thumbnail fetch failed (non-fatal): ${
        err instanceof Error ? err.message : String(err)
      }`
    );
    return undefined;
  }
}

interface BrandDeckBootstrap {
  presentationRef: FactSetPresentationRef | null;
  /** True only when a fresh brand deck was actually opened and targeted. Always false: builds stay in the current deck. */
  opened: boolean;
  /** Ids of opened brand-template slides; unused when not opening a replacement deck. */
  bootstrapSlideIds: Set<string>;
  /** id -> open-time content fingerprint, for the content-aware end-clean. */
  bootstrapFingerprintById: Map<string, string>;
  /** Reference-deck examples (per-slide spec + image) for the seed turn, if gathered. */
  referenceExamples: ReferenceDeckExamples | null;
  /** Pre-fetched brand layout catalog, injected if the windowed extract lacks it. */
  deckLayoutCatalog: DeckLayoutCatalog | null;
}

/**
 * Gather brand design inspiration in the current deck: temporarily insert the
 * reference template (after the last existing slide), extract, then strip only
 * those inserted ids. Never opens a replacement presentation or deletes the
 * user's existing slides.
 */
async function bootstrapBrandDeck(
  brand: BrandConfig,
  initialRef: FactSetPresentationRef | null,
  log: (message: string) => void
): Promise<BrandDeckBootstrap> {
  const empty: BrandDeckBootstrap = {
    presentationRef: initialRef,
    opened: false,
    bootstrapSlideIds: new Set<string>(),
    bootstrapFingerprintById: new Map(),
    referenceExamples: null,
    deckLayoutCatalog: null,
  };

  if (!shouldUseFactSetBridge() || !brand.referenceTemplateAsset) {
    return empty;
  }

  let referenceExamples: ReferenceDeckExamples | null = null;
  try {
    const resp = await fetch(brand.referenceTemplateAsset);
    if (resp.ok) {
      const bytes = new Uint8Array(await resp.arrayBuffer());
      let binary = "";
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const curatedSlides =
        brand.referenceMode === "curated-images" ? (brand.referenceSlides ?? []) : [];
      const isCurated = curatedSlides.length > 0;
      const referenceDeck = await parsePptxBase64AsReference(btoa(binary), log, {
        renderImages: true,
        ...(isCurated
          ? { imageSlideIndexes: curatedSlides.map((slide) => slide.slideNumber - 1) }
          : { maxImages: REFERENCE_DECK_IMAGE_MAX }),
        imageHeight: REFERENCE_DECK_IMAGE_HEIGHT,
        presentationRef: initialRef,
      });
      if (referenceDeck.slides.length > 0) {
        const referenceImages = referenceDeck.referenceImages ?? [];
        if (isCurated) {
          referenceExamples = buildCuratedReferenceExamples(
            curatedSlides,
            curatedSlides.map((slide) => referenceImages[slide.slideNumber - 1])
          );
        } else {
          referenceExamples = buildReferenceDeckExamples(referenceDeck, referenceImages);
        }
        const renderedImageCount = referenceExamples.slides.filter((slide) => slide.image).length;
        log(
          `[agentic-deck] gathered ${referenceExamples.slides.length} ${brand.label} reference page(s) (${renderedImageCount} image(s)) for inspiration`
        );
      }
    } else {
      log(`[agentic-deck] could not fetch ${brand.label} reference template: ${resp.status}`);
    }
  } catch (error) {
    log(
      `[agentic-deck] reference inspiration gather failed (non-fatal): ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  return {
    ...empty,
    referenceExamples,
  };
}

export interface RunAgenticDeckBuildParams {
  plan: DeckPlan;
  onProgress: (p: CreationProgress) => void;
  brandId: BrandId | null;
  presentationRef: FactSetPresentationRef | null;
  insertAfterCurrent: boolean;
  styleConfig?: StyleConfig | null;
  /** Abort signal owned by the caller (createDeck) so cancellation works. */
  signal: AbortSignal;
  log: (message: string) => void;
  syncTabsWithPresentation: (reason: string) => Promise<void>;
  setAiMode: (mode: "chat" | "create" | "review") => void;
  /** Optional: lets the build attach per-slide citation messages (task decks). */
  setTabsState?: (updater: (prev: TabsState) => TabsState) => void;
  /** Per-run Langfuse context for deck-build requests and outcomes. */
  langfuseContext?: PptxLangfuseRunContext;
  companyId?: string | null;
}

export async function runAgenticDeckBuild(params: RunAgenticDeckBuildParams): Promise<void> {
  const {
    plan,
    onProgress,
    brandId,
    presentationRef,
    insertAfterCurrent,
    styleConfig,
    signal,
    log,
    syncTabsWithPresentation,
    setAiMode,
    setTabsState,
    langfuseContext,
    companyId,
  } = params;
  const total = plan.slides.length;
  const hasActiveSkill = Boolean(plan.skillRunContext);
  const brand = brandId ? BRANDS[brandId] : null;
  // Brand builds stay in the current deck. These track gathered reference
  // inspiration; bootstrap seed cleanup is unused because we never open a
  // replacement file.
  let effectivePresentationRef = presentationRef;
  let brandDeckOpened = false;
  let bootstrapSlideIds = new Set<string>();
  let bootstrapFingerprintById = new Map<string, string>();
  let referenceExamples: ReferenceDeckExamples | null = null;
  let brandDeckLayoutCatalog: DeckLayoutCatalog | null = null;
  const goldenArtifactRun = isGoldenArtifactCaptureEnabled() ? createGoldenArtifactRun() : null;
  if (goldenArtifactRun) {
    log(`[GOLDEN] Deck creation capture enabled for run ${goldenArtifactRun.runId}`);
  }

  // Per-slide progress is approximate: the agent builds the whole deck in one
  // streaming loop, so we advance the slide counter by the number of NEW slides
  // each apply_edits call inserts, stream its thinking/tool activity into
  // recentLogs, and map the trailing get_slides_info review to finalReview.
  let slidesBuilt = 0;
  // Faithful, order-independent build tracking: real host slide ids created this
  // turn (dedupes batched/duplicate/out-of-order inserts); refinement passes are
  // modify-only applies that must not inflate or regress progress.
  const builtSlideIds = new Set<string>();
  let refinementPasses = 0;
  const recentLogs: string[] = [];
  const agentEvents: AgentProgressEvent[] = [];
  let latestAgentTimeline = emptyTimeline();
  const toolEventIdsByName = new Map<string, string>();
  let agentEventCounter = 0;
  let reasoningEventId: string | null = null;
  let agentSummary: string | undefined;
  const pushLog = (line: string): void => {
    const trimmed = line.trim();
    if (!trimmed) return;
    recentLogs.push(trimmed);
    if (recentLogs.length > 30) recentLogs.shift();
  };
  const pushAgentEvent = (
    event: Omit<AgentProgressEvent, "id"> & { id?: string }
  ): AgentProgressEvent => {
    const next: AgentProgressEvent = {
      ...event,
      id: event.id ?? `agent-event-${++agentEventCounter}`,
    };
    agentEvents.push(next);
    if (agentEvents.length > 80) agentEvents.shift();
    return next;
  };
  const classifyToolResult = (result: ToolResult): "ok" | "warning" | "error" => {
    if (!result.success) return "error";
    const summary = (result.data as { summary?: { errors?: { count?: number }; skipped?: { count?: number } } })
      ?.summary;
    if ((summary?.errors?.count ?? 0) > 0 || (summary?.skipped?.count ?? 0) > 0) return "warning";
    return "ok";
  };
  const summarizeToolResult = (result: ToolResult): string | undefined => {
    if (!result.success && result.error) return result.error;
    const summary = (result.data as { summary?: { applied?: { slides?: number; shapes?: number } } })?.summary;
    if (summary?.applied) {
      return `Applied ${summary.applied.shapes ?? 0} shape change(s) across ${summary.applied.slides ?? 0} slide(s).`;
    }
    if (result.error) return result.error;
    return undefined;
  };

  // Best-effort citation capture for task-grounded decks: accumulate every
  // apply_edits call's real-id-keyed citation bindings (from the apply pipeline
  // manifest). Real ids mean no createdSlides diff / positional pairing is
  // needed. Cited values are merged across calls.
  const citationBindings: SlideCitationBinding[] = [];
  const declaredCitedValues: Record<string, string> = {};

  type EmitOpts = {
    currentSlide?: number;
    phase?: CreationProgress["phase"];
    finalReview?: { current: number; total: number };
    polish?: CreationProgress["polish"];
  };
  let lastEmitArgs: { status: CreationProgress["status"]; message: string; opts?: EmitOpts } = {
    status: "generating",
    message: "Designing slides...",
  };
  const emit = (status: CreationProgress["status"], message: string, opts?: EmitOpts): void => {
    lastEmitArgs = { status, message, opts };
    const currentSlide = opts?.currentSlide ?? Math.min(slidesBuilt, Math.max(0, total - 1));
    const completedSlides = plan.slides.slice(0, Math.min(slidesBuilt, total)).map((slide) => slide.title);
    onProgress({
      currentSlide,
      totalSlides: total,
      currentSlideTitle: plan.slides[Math.min(currentSlide, Math.max(0, total - 1))]?.title || "",
      status,
      message,
      completedSlides,
      recentLogs: [...recentLogs],
      agentEvents: [...agentEvents],
      agentTimeline: latestAgentTimeline,
      streamApplyEditSlideSummaries: true,
      ...(agentSummary ? { agentSummary } : undefined),
      ...(opts?.phase ? { phase: opts.phase } : undefined),
      ...(opts?.finalReview ? { finalReview: opts.finalReview } : undefined),
      ...(opts?.polish ? { polish: opts.polish } : undefined),
      // Carry the build presentation so the App's deck-creation UI tracking
      // stays on the current deck.
      ...(effectivePresentationRef?.openPresentationId
        ? { targetPresentationId: effectivePresentationRef.openPresentationId }
        : undefined),
      ...(effectivePresentationRef?.name || effectivePresentationRef?.fullName
        ? {
            targetPresentationName:
              effectivePresentationRef.name || effectivePresentationRef.fullName,
          }
        : undefined),
    });
  };

  // Re-emit (throttled) so per-token agent-timeline deltas reach the deck UI
  // smoothly instead of only on coarse per-hop emits.
  let timelineFlushPending = false;
  const scheduleTimelineEmit = (): void => {
    if (timelineFlushPending) return;
    timelineFlushPending = true;
    const flush = (): void => {
      timelineFlushPending = false;
      emit(lastEmitArgs.status, lastEmitArgs.message, lastEmitArgs.opts);
    };
    if (typeof requestAnimationFrame !== "undefined") requestAnimationFrame(flush);
    else setTimeout(flush, 50);
  };

  pushAgentEvent({
    type: "tool",
    label: "Preparing deck build",
    detail: `Approved plan has ${total} slide${total === 1 ? "" : "s"}.`,
    status: "running",
  });
  if (hasActiveSkill) {
    pushAgentEvent({
      type: "observation",
      label: "Applying selected Skill in PowerPoint",
      detail:
        "Applicable instructions remain active; unsupported output mechanics are adapted to the available PowerPoint tools.",
      status: "running",
    });
  }
  emit("generating", "Starting agentic deck build...");

  // Snapshot the user's selected slide before in-place brand gathering.
  // Temp insert/delete can leave the host on the last remaining slide, and
  // the post-gather windowed extract would then anchor `insertAfterCurrent`
  // on the wrong id.
  let preGatherSelectedSlideId: string | null = null;
  if (insertAfterCurrent && brand && shouldUseFactSetBridge()) {
    try {
      const selected = await getSelectedSlideRefViaIntents(log, {
        ...(presentationRef ? { presentationRef } : {}),
      });
      const slideId = typeof selected?.id === "string" ? selected.id.trim() : "";
      preGatherSelectedSlideId = slideId || null;
    } catch (error) {
      log(
        `[agentic-deck] could not snapshot selected slide before brand gather (non-fatal): ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  // Brand build (FactSet-only): gather reference-deck design inspiration in
  // the current deck (temp insert -> extract -> strip) before the build.
  if (brand) {
    emit("generating", `Preparing ${brand.label} template...`);
    const bootstrap = await bootstrapBrandDeck(brand, presentationRef, log);
    effectivePresentationRef = bootstrap.presentationRef;
    brandDeckOpened = bootstrap.opened;
    bootstrapSlideIds = bootstrap.bootstrapSlideIds;
    bootstrapFingerprintById = bootstrap.bootstrapFingerprintById;
    referenceExamples = bootstrap.referenceExamples;
    brandDeckLayoutCatalog = bootstrap.deckLayoutCatalog;
  }

  let content;
  try {
    content = await extractWindowedPresentationContent(log, {
      forceRefresh: true,
      windowSize: CHAT_CONTEXT_WINDOW_SIZE,
      presentationRef: effectivePresentationRef ?? undefined,
    });
  } catch (error) {
    log(`[agentic-deck] extract failed: ${error instanceof Error ? error.message : String(error)}`);
    emit("error", "Could not read the presentation to build the deck.");
    return;
  }

  // Fallback: if the windowed extract could not attach the brand deck's layout
  // catalog (the by-ref fetch can fail for a just-opened deck), use the one
  // pre-fetched during bootstrap so the agent still receives the available
  // branded layouts to choose from.
  if (
    brandDeckLayoutCatalog &&
    brandDeckLayoutCatalog.layouts.length > 0 &&
    !content.deckLayoutCatalog?.layouts?.length
  ) {
    content.deckLayoutCatalog = brandDeckLayoutCatalog;
    log("[agentic-deck] attached pre-fetched brand layout catalog to deck context");
  }

  const requestedExistingBaseSlides = plan.slides
    .map((slide, planIndex) => ({
      planIndex,
      baseSlideIndex: !slide.librarySlideId && typeof slide.baseSlideIndex === "number" ? slide.baseSlideIndex : null,
    }))
    .filter((entry): entry is { planIndex: number; baseSlideIndex: number } => entry.baseSlideIndex !== null);

  // Build-start snapshot of existing slide ids. The post-build polish phase uses
  // this (a build-start vs build-end deck diff) to identify slides created in
  // THIS build, which is robust to the partial-apply gating that can leave some
  // created ids out of builtSlideIds.
  const initialDeckSlideIds = new Set<string>();
  let initialSlides: SlideRef[] = [];
  try {
    initialSlides = await listSlides({
      forceRefresh: true,
      presentationRef: effectivePresentationRef ?? undefined,
    });
    for (const slide of initialSlides) initialDeckSlideIds.add(slide.id);
  } catch (error) {
    log(
      `[agentic-deck] failed to snapshot initial slides for polish diff (non-fatal): ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    if (requestedExistingBaseSlides.length > 0) {
      emit(
        "error",
        "Could not resolve the selected base slide because the presentation slide list could not be loaded."
      );
      return;
    }
  }
  const initialSlidesByIndex = new Map(initialSlides.map((slide) => [slide.index, slide]));

  // Anchor on the user's SELECTED slide (its true deck index), not the last
  // slide in the centered extraction window - those diverge for decks larger
  // than the window. `extractWindowedPresentationContent` overrides
  // `selectedSlideIndex` to the true deck index. Prefer the pre-gather
  // snapshot when brand gathering may have moved host selection.
  const liveSelectedId =
    content.slides.find((slide) => slide.index === content.selectedSlideIndex)?.id ?? null;
  const snapshotStillInDeck =
    preGatherSelectedSlideId != null &&
    (initialSlides.some((slide) => slide.id === preGatherSelectedSlideId) ||
      content.slides.some((slide) => slide.id === preGatherSelectedSlideId));
  const anchorSlideId = brandDeckOpened
    ? null
    : insertAfterCurrent
      ? snapshotStillInDeck
        ? preGatherSelectedSlideId
        : liveSelectedId
      : null;

  // Resolve user-selected base slides deterministically so the agent honors
  // them in plan order: existing-deck slides (baseSlideIndex -> real id; the
  // agent duplicates them) and slide-library slides (librarySlideId -> pptx
  // sources; the agent inserts them via the insert_library_slide tool).
  const baseSlideIdByPlanIndex: Record<number, string> = {};
  const librarySlideSourcesById = new Map<string, { title?: string; pptxSources: string[] }>();
  const librarySlideCache = new Map<string, SlideLibrarySlide>();
  const libraryValidationContext: AgentToolContext = {
    companyId: companyId || brand?.companyId || undefined,
    librarySlideSourcesById,
    librarySlideCache,
  };
  const unresolvedBaseSlides: Array<{ planIndex: number; baseSlideIndex: number }> = [];
  const unresolvedLibrarySlides: Array<{ planIndex: number; label: string }> = [];
  for (const [i, slide] of plan.slides.entries()) {
    if (slide.librarySlideId) {
      const remembered = slide.rememberedLibrarySlide as
        | {
            collectionKind?: string;
            pptxUrl?: string;
            pptxUrlCandidates?: string[];
          }
        | undefined;
      if (remembered?.collectionKind === "preslink_template") continue;
      const planPptxSources = collectPlanLibraryPptxSources({
        librarySlidePptxUrl: slide.librarySlidePptxUrl,
        librarySlidePptxUrlCandidates: slide.librarySlidePptxUrlCandidates,
        rememberedLibrarySlide: remembered,
      });
      let resolvedSource: { title?: string; pptxSources: string[] } | null =
        planPptxSources.length > 0
          ? {
              title: slide.librarySlideTitle ?? slide.title,
              pptxSources: planPptxSources,
            }
          : null;
      if (!resolvedSource) {
        try {
          resolvedSource = await resolveLibrarySlidePptxSources(
            slide.librarySlideId,
            libraryValidationContext
          );
        } catch (error) {
          log(
            `[agentic-deck] library slide lookup failed for ${slide.librarySlideId}: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
          unresolvedLibrarySlides.push({
            planIndex: i,
            label: slide.librarySlideTitle || slide.librarySlideId,
          });
          continue;
        }
      }
      if (!resolvedSource || resolvedSource.pptxSources.length === 0) {
        if (slide.librarySlideThumbnailUrl?.trim()) {
          continue;
        }
        unresolvedLibrarySlides.push({
          planIndex: i,
          label: slide.librarySlideTitle || slide.librarySlideId,
        });
        continue;
      }
      librarySlideSourcesById.set(slide.librarySlideId, {
        title: resolvedSource.title ?? slide.librarySlideTitle ?? slide.title,
        pptxSources: resolvedSource.pptxSources,
      });
      continue;
    }
    if (typeof slide.baseSlideIndex === "number") {
      const match = initialSlidesByIndex.get(slide.baseSlideIndex);
      if (match?.id) {
        baseSlideIdByPlanIndex[i] = match.id;
      } else {
        unresolvedBaseSlides.push({ planIndex: i, baseSlideIndex: slide.baseSlideIndex });
      }
    }
  }
  if (unresolvedLibrarySlides.length > 0) {
    const details = unresolvedLibrarySlides
      .map(({ planIndex, label }) => `plan slide ${planIndex + 1} ("${label}")`)
      .join(", ");
    const message = `Could not resolve selected library slide(s): ${details}. Check the slide library connection and try again.`;
    log(`[agentic-deck] ${message}`);
    emit("error", message);
    return;
  }
  if (unresolvedBaseSlides.length > 0) {
    const details = unresolvedBaseSlides
      .map(
        ({ planIndex, baseSlideIndex }) =>
          `plan slide ${planIndex + 1} -> deck slide ${baseSlideIndex + 1}`
      )
      .join(", ");
    const message = `Could not resolve selected base slide(s): ${details}. Please reselect the base slide and try again.`;
    log(`[agentic-deck] ${message}`);
    emit("error", message);
    return;
  }

  // Base-slide grounding: build "BASE SLIDE TEMPLATES" examples (full shape JSON +
  // rendered image) for each unique user-selected base slide, delivered via the
  // reference-examples interleaving channel so the agent can match what it
  // duplicates/inserts. Skipped when brand reference examples already occupy
  // that channel.
  let baseTemplateExamples: ReferenceDeckExamples | null = null;
  if (!brandDeckOpened && !referenceExamples) {
    const exampleSlides: ReferenceDeckExamples["slides"] = [];

    // Bind each captured base template to the plan slide(s) it will fill, so the
    // label tells the agent exactly which slide(s) a template is for (one base can
    // be reused by several plan slides) and freeform slides are not modeled on it.
    const fmtTargetSlides = (nums: number[] | undefined): string =>
      !nums || nums.length === 0
        ? "an unassigned slide"
        : nums.length === 1
          ? `Slide ${nums[0]}`
          : `Slides ${nums.join(", ")}`;
    const existingTargetsByBaseId = new Map<string, number[]>();
    const libraryTargetsById = new Map<string, number[]>();
    plan.slides.forEach((slide, i) => {
      const targetBaseSlideId = baseSlideIdByPlanIndex[i];
      if (targetBaseSlideId && typeof slide.baseSlideIndex === "number") {
        const arr = existingTargetsByBaseId.get(targetBaseSlideId) ?? [];
        arr.push(i + 1);
        existingTargetsByBaseId.set(targetBaseSlideId, arr);
      }
      if (slide.librarySlideId) {
        const arr = libraryTargetsById.get(slide.librarySlideId) ?? [];
        arr.push(i + 1);
        libraryTargetsById.set(slide.librarySlideId, arr);
      }
    });

    // Existing-deck base slides: scoped extract BY ID (full detail) + render BY INDEX.
    const seenBaseSlideIds = new Set<string>();
    const existingBases: Array<{ baseSlideId: string; baseIndex: number }> = [];
    plan.slides.forEach((slide, i) => {
      const baseSlideId = baseSlideIdByPlanIndex[i];
      const baseIndex = slide.baseSlideIndex;
      if (!baseSlideId || typeof baseIndex !== "number" || seenBaseSlideIds.has(baseSlideId)) {
        return;
      }
      seenBaseSlideIds.add(baseSlideId);
      existingBases.push({ baseSlideId, baseIndex });
    });
    for (const { baseSlideId, baseIndex } of existingBases) {
      try {
        const extracted = await extractPresentationContent(log, {
          forceRefresh: true,
          filters: { slides: { ids: [baseSlideId] }, pageSetup: true },
          presentationRef: effectivePresentationRef ?? undefined,
        });
        const baseSlide = extracted.slides[0];
        if (!baseSlide) continue;
        const image = await exportSlideAsImage(baseIndex, {
          presentationRef: effectivePresentationRef ?? undefined,
        });
        exampleSlides.push({
          label: `=== Base template for ${fmtTargetSlides(existingTargetsByBaseId.get(baseSlideId))} (DUPLICATE deck slide ${baseIndex + 1}) ===`,
          json: JSON.stringify(baseSlide.shapes ?? [], null, 2),
          image: image || undefined,
        });
      } catch (err) {
        log(
          `[agentic-deck] base slide capture failed for deck slide ${baseIndex + 1} (non-fatal): ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      }
    }

    // Library base slides: fetch the pptx and parse it as a reference (temp
    // insert -> extract -> strip) for full shape JSON + a rendered image. On
    // failure, fall back to the saved thumbnail image (no JSON).
    // Iterate EVERY selected library slide, not just those with pptx sources, so a
    // thumbnail-only selection (no pptx candidates) is still grounded via the
    // thumbnail fallback below instead of being skipped entirely. pptxSources/title
    // come from librarySlideSourcesById when available; thumbnail-only slides
    // resolve to an empty pptxSources list and fall straight through to the thumbnail.
    const librarySelectionsById = new Map<
      string,
      { title?: string; pptxSources: string[]; thumbnailUrl?: string }
    >();
    plan.slides.forEach((slide) => {
      const id = slide.librarySlideId;
      if (!id || librarySelectionsById.has(id)) return;
      const resolved = librarySlideSourcesById.get(id);
      librarySelectionsById.set(id, {
        title: resolved?.title ?? slide.librarySlideTitle ?? slide.title,
        pptxSources: resolved?.pptxSources ?? [],
        thumbnailUrl: slide.librarySlideThumbnailUrl,
      });
    });
    for (const [librarySlideId, source] of Array.from(librarySelectionsById.entries())) {
      let captured = false;
      const pptxUrl = source.pptxSources[0];
      if (pptxUrl) {
        try {
          const resp = await fetch(pptxUrl);
          if (resp.ok) {
            const bytes = new Uint8Array(await resp.arrayBuffer());
            let binary = "";
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const refDeck = await parsePptxBase64AsReference(btoa(binary), log, {
              renderImages: true,
              maxImages: 1,
              imageHeight: REFERENCE_DECK_IMAGE_HEIGHT,
              presentationRef: effectivePresentationRef,
            });
            const refSlide = refDeck.slides[0];
            if (refSlide) {
              exampleSlides.push({
                label: `=== Base template for ${fmtTargetSlides(libraryTargetsById.get(librarySlideId))} (LIBRARY "${source.title ?? librarySlideId}", id ${librarySlideId}) ===`,
                json: JSON.stringify(refSlide.shapes ?? [], null, 2),
                image: refDeck.referenceImages?.[0],
              });
              captured = true;
            }
          }
        } catch (err) {
          log(
            `[agentic-deck] library base capture failed for ${librarySlideId} (non-fatal): ${
              err instanceof Error ? err.message : String(err)
            }`
          );
        }
      }
      if (!captured) {
        // Fallback: thumbnail image only, omit JSON.
        const thumbUrl = source.thumbnailUrl;
        const thumbImage = thumbUrl ? await fetchImageUrlAsBase64(thumbUrl, log) : undefined;
        if (thumbImage) {
          exampleSlides.push({
            label: `=== Base template for ${fmtTargetSlides(libraryTargetsById.get(librarySlideId))} (LIBRARY "${source.title ?? librarySlideId}", id ${librarySlideId}) ===`,
            json: "(template preview unavailable - match the attached thumbnail and fill it in place)",
            image: thumbImage,
          });
        }
      }
    }

    if (exampleSlides.length > 0) {
      baseTemplateExamples = buildBaseTemplateExamples(exampleSlides);
    }
  }

  // Brand reference examples and base-slide templates flow through the same
  // reference-examples channel; brand examples win when both were gathered.
  const effectiveReferenceExamples = referenceExamples ?? baseTemplateExamples;

  const seed = buildDeckBuildSeedPrompt(plan, {
    anchorSlideId,
    insertAfterCurrent,
    styleDescription: styleConfig?.description ?? null,
    hasReferenceExamples: Boolean(referenceExamples && referenceExamples.slides.length > 0),
    hasBaseSlideTemplates: Boolean(baseTemplateExamples && baseTemplateExamples.slides.length > 0),
    baseSlideIdByPlanIndex,
  });

  const useFactSetBridge = shouldUseFactSetBridge();
  const allowActiveGraph =
    isPowerPointActiveGraphsEnabled() && useFactSetBridge && isPowerPointNativeAgentActive();
  const baseToolPolicy: ToolExecutionPolicy = {
    ...(brandId ? { brandId } : undefined),
    allowPreslink: useFactSetBridge,
    enableSnippetGuidance: resolveSnippetPolicy({ brandId }).enabled,
    publicBrandName: useFactSetBridge ? "FactSet AI" : "Finster",
  };
  const {
    buildToolPolicy,
    buildAgentToolContext: skillRuntimeContext,
    preludeUserMessages,
    polishToolPolicy,
    blockedMessage: skillBlockedMessage,
    omittedTargetCount: skillOmittedTargetCount = 0,
  } = buildDeckBuildSkillRuntime(plan, baseToolPolicy);
  const toolPolicy: ToolExecutionPolicy = {
    ...buildToolPolicy,
    ...(allowActiveGraph ? { allowActiveGraph: true } : {}),
  };
  if (skillBlockedMessage) {
    // Fail closed before the build sends anything: the plan was approved with
    // this Skill, so running it with part of its target files withheld would
    // produce a deck the user believes was built to the whole Skill.
    log(`[agentic-deck] blocked: ${skillBlockedMessage}`);
    emit("error", skillBlockedMessage);
    return;
  }
  if (skillOmittedTargetCount > 0) {
    log(
      `[agentic-deck] Skill target files not attached: ${skillOmittedTargetCount} file(s) would exceed the per-request limit. Continuing without them.`
    );
  }
  const planningEvidence = plan.skillRunContext ? plan.planningEvidence : undefined;
  const groundingCitations = unionGroundingCitations(
    plan.groundingCitations,
    planningEvidence?.citations
  );
  let groundingCitationsMerged = false;
  const mergeGroundingCitationsIntoPool = async (): Promise<void> => {
    if (
      groundingCitationsMerged ||
      !plan.skillRunContext ||
      !groundingCitations?.length
    ) {
      return;
    }
    try {
      await mergeIntoPool(groundingCitations, log);
      groundingCitationsMerged = true;
    } catch (error) {
      log(
        `[agentic-deck] grounding citation pool merge failed (non-fatal): ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  };
  await mergeGroundingCitationsIntoPool();

  await runGoldenCaptureStep(goldenArtifactRun, log, "before.pptx", async () => {
    const beforeDeckBase64 = await exportPresentationAsBase64Compressed();
    await uploadGoldenArtifactPptx(goldenArtifactRun!.runId, "before", beforeDeckBase64);
  });
  await runGoldenCaptureStep(goldenArtifactRun, log, "deck_plan.json", async () => {
    await uploadGoldenArtifactJson(goldenArtifactRun!.runId, "deck_plan", {
      runId: goldenArtifactRun!.runId,
      plannerRunId: plan.plannerRunId ?? null,
      deckBuildRunId: langfuseContext?.runId ?? null,
      generatedAt: new Date().toISOString(),
      topic: plan.topic,
      slideCount: plan.slides.length,
      plannerOriginalCount: plan.plannerOriginalCount ?? plan.slides.length,
      plannerRetries: plan.plannerRetries ?? 0,
      brandId: brandId ?? null,
      insertAfterCurrent,
      anchorSlideId,
      plan,
    });
  });
  await runGoldenCaptureStep(goldenArtifactRun, log, "llm_input.json", async () => {
    await uploadGoldenArtifactJson(goldenArtifactRun!.runId, "llm_input", {
      runId: goldenArtifactRun!.runId,
      plannerRunId: plan.plannerRunId ?? null,
      deckBuildRunId: langfuseContext?.runId ?? null,
      generatedAt: new Date().toISOString(),
      topic: plan.topic,
      seed,
      preludeUserMessages: preludeUserMessages ?? null,
      skillContext: plan.skillRunContext
        ? {
            selectedSkillsGuidance: skillRuntimeContext.selectedSkillsGuidance ?? null,
            finsterDataSources: toolPolicy.finsterDataSources ?? null,
            userFileIds: toolPolicy.userFileIds ?? null,
          }
        : null,
      content,
      toolPolicy,
      brandId: brandId ?? null,
      insertAfterCurrent,
      anchorSlideId,
      style: styleConfig
        ? {
            hasPdfStyleImage: Boolean(styleConfig.pdfStyleImage),
            pdfStyleImageBytesBase64Length: styleConfig.pdfStyleImage?.length ?? 0,
            description: styleConfig.description ?? null,
          }
        : null,
      referenceExamples: effectiveReferenceExamples
        ? {
            slideCount: effectiveReferenceExamples.slides.length,
            imageCount: effectiveReferenceExamples.slides.filter((slide) => slide.image).length,
            slides: effectiveReferenceExamples.slides.map((slide) => ({
              label: slide.label,
              jsonLength: slide.json?.length ?? 0,
              hasImage: Boolean(slide.image),
            })),
          }
        : null,
      grounding: {
        hasGroundingContext: Boolean(plan.groundingContextForSlides?.trim()),
        groundingMessage: plan.groundingMessage ?? null,
        groundingCitationCount: groundingCitations?.length ?? 0,
        planningEvidenceCount: planningEvidence?.count ?? 0,
      },
    });
  });

  const nextGoldenToolSubdirectory = createToolArtifactSubdirectoryAllocator();

  const agentToolContext: AgentToolContext = {
    ...skillRuntimeContext,
    userPrompt: seed,
    presentationRef: effectivePresentationRef,
    ...(allowActiveGraph ? { activeGraphCreateResults: new Map() } : {}),
    // Pin the target presentation's page geometry so apply_edits sizes shapes
    // and tables to the real slide, not the 960x540 default.
    slideBounds:
      content.slideWidth && content.slideHeight
        ? { slideWidth: content.slideWidth, slideHeight: content.slideHeight }
        : null,
    signal,
    goldenArtifactRun,
    nextGoldenToolSubdirectory,
    log,
    librarySlideSourcesById,
    companyId: companyId || brand?.companyId || undefined,
    librarySlideCache,
    onProgress: (message: string) => emit("generating", message),
    reportApplyProgress: ({ insertedCount, created, modifiedIds }) => {
      const createdIds = (created ?? []).map((slide) => slide.id).filter(Boolean);
      const newlyCreated = createdIds.filter((id) => !builtSlideIds.has(id));
      for (const id of createdIds) builtSlideIds.add(id);
      // Prefer the real host-id count (order-independent, dedupes batched and
      // duplicate inserts); fall back to the positional counter when the host
      // diff could not resolve created slide ids.
      const realInserted = createdIds.length > 0 ? newlyCreated.length : insertedCount;
      if (realInserted > 0) {
        slidesBuilt =
          builtSlideIds.size > 0
            ? Math.min(builtSlideIds.size, total)
            : Math.min(slidesBuilt + realInserted, total);
        pushAgentEvent({
          type: "tool",
          label: `Created ${realInserted} slide${realInserted === 1 ? "" : "s"}`,
          detail: `Deck progress is now ${Math.min(slidesBuilt, total)} of ${total} slides.`,
          status: "ok",
          hidden: true,
        });
        emit("generating", `Built slide ${Math.min(slidesBuilt, total)} of ${total}...`);
      } else if (modifiedIds && modifiedIds.length > 0) {
        // Modify-only refinement pass: show motion without advancing the count.
        refinementPasses += 1;
        emit("generating", `Refining slides (pass ${refinementPasses})...`);
      } else {
        emit("generating", `Built slide ${Math.min(slidesBuilt, total)} of ${total}...`);
      }
    },
    reportCitationBindings: ({ bindings, citedValuesByUuid }) => {
      for (const [id, value] of Object.entries(citedValuesByUuid)) {
        declaredCitedValues[id] = value;
      }
      citationBindings.push(...bindings);
    },
  };

  // Post-build per-slide visual polish phase: silently replay a chat-mode "fix
  // obvious visual defects" message on each created slide, bounded-parallel, and
  // surface it via the deck-creation UI "polishing" phase. Closes over emit /
  // pushAgentEvent / agentEvents and the build-start snapshot.
  const runPostBuildVisualPolish = async (): Promise<void> => {
    if (!isPostBuildVisualPolishEnabled() || signal.aborted) return;
    let currentSlides: SlideRef[];
    try {
      currentSlides = await listSlides({
        forceRefresh: true,
        presentationRef: effectivePresentationRef ?? undefined,
      });
    } catch (error) {
      log(
        `[agentic-deck] polish: could not list slides (${
          error instanceof Error ? error.message : String(error)
        })`
      );
      return;
    }

    const slidesToPolish = identifyCreatedSlidesForPolish({
      current: currentSlides,
      initialDeckSlideIds,
      builtSlideIds,
      bootstrapSlideIds,
      planSlides: plan.slides,
    });
    if (slidesToPolish.length === 0) return;

    const polishTotal = slidesToPolish.length;
    let polishCompleted = 0;
    const slideStatuses: NonNullable<CreationProgress["polish"]>["slideStatuses"] = {};
    const slideTitles = Object.fromEntries(
      slidesToPolish.map((slide) => [slide.planPosition, slide.title])
    );
    const polishEventIdByPosition = new Map<number, string>();
    const emitPolish = (message: string): void => {
      emit("generating", message, {
        currentSlide: Math.max(0, total - 1),
        phase: "polishing",
        polish: {
          current: Math.min(polishCompleted, polishTotal),
          total: polishTotal,
          slideStatuses: { ...slideStatuses },
          slideTitles,
        },
      });
    };

    pushAgentEvent({
      type: "tool",
      label: "Polishing slides",
      detail: `Reviewing ${polishTotal} created slide${
        polishTotal === 1 ? "" : "s"
      } for visual defects.`,
      status: "running",
    });
    emitPolish("Polishing slides...");

    const polishSlideBounds =
      content.slideWidth && content.slideHeight
        ? { slideWidth: content.slideWidth, slideHeight: content.slideHeight }
        : null;

    await runWithConcurrency(
      slidesToPolish,
      getPostBuildPolishConcurrency(),
      async (slide) => {
        if (signal.aborted) return;
        slideStatuses[slide.planPosition] = "active";
        // No toolName: the activity panel maps tool names to generic labels
        // (get_slides_info -> "Checking layout"), which would mask the per-slide
        // polish label. Rely on event.label + status-driven icon instead.
        const event = pushAgentEvent({
          type: "observation",
          label: `Polishing slide ${slide.planPosition}`,
          detail: slide.title,
          status: "running",
          slideNumber: slide.planPosition,
        });
        polishEventIdByPosition.set(slide.planPosition, event.id);
        emitPolish(`Polishing slide ${slide.planPosition} of ${polishTotal}...`);
        try {
          await runSlideVisualPolish({
            slide,
            deckSlides: currentSlides,
            presentationRef: effectivePresentationRef ?? null,
            slideBounds: polishSlideBounds,
            signal,
            toolPolicy: polishToolPolicy,
            log,
            goldenArtifactRun,
            goldenSubdirectoryPrefix: `polish_s${String(slide.planPosition).padStart(2, "0")}`,
            langfuseContext,
          });
          slideStatuses[slide.planPosition] = "done";
          const existingId = polishEventIdByPosition.get(slide.planPosition);
          const existing = existingId ? agentEvents.find((item) => item.id === existingId) : undefined;
          if (existing) {
            existing.label = `Polished slide ${slide.planPosition}`;
            existing.status = "ok";
          }
        } catch (error) {
          slideStatuses[slide.planPosition] = "error";
          const existingId = polishEventIdByPosition.get(slide.planPosition);
          const existing = existingId ? agentEvents.find((item) => item.id === existingId) : undefined;
          const detail = error instanceof Error ? error.message : String(error);
          if (existing) {
            existing.type = "warning";
            existing.status = "warning";
            existing.detail = detail;
          }
          log(`[agentic-deck] polish slide ${slide.planPosition} failed: ${detail}`);
        } finally {
          polishCompleted += 1;
          emitPolish(`Polished ${polishCompleted} of ${polishTotal} slides...`);
        }
      },
      { signal }
    );
  };

  await new Promise<void>((resolve) => {
    // Settle exactly once. queryOpenAIStreaming normally drives this via
    // onComplete/onError, but it is async: if its promise rejects WITHOUT
    // invoking either callback (bad model config, a synchronous throw during
    // argument prep, or an internal unhandled error), the `.catch` below
    // settles us instead. Without it this promise - and the createDeck
    // `finally` that clears isCreatingDeck - would hang forever and lock the UI.
    let settled = false;
    const finish = (errorMessage?: string): void => {
      if (settled) return;
      settled = true;
      if (errorMessage !== undefined) emit("error", errorMessage);
      resolve();
    };
    const narratorSink = createNarratingAgentEventSink({
      config: getReasoningNarrationConfig(),
      userQuery: seed,
      signal,
      log,
      initialCue: `The agent is starting the ${total}-slide deck build now.`,
      onThinking: (text) => {
        const detail = text.trim();
        if (!detail) return;
        log(`[agentic-deck] ${detail}`);
        pushLog(detail.slice(0, 500));
        const existingReasoningEvent = reasoningEventId
          ? agentEvents.find((event) => event.id === reasoningEventId)
          : undefined;
        if (existingReasoningEvent) {
          existingReasoningEvent.detail = detail;
          existingReasoningEvent.detailFormat = "markdown";
          existingReasoningEvent.status = "running";
        } else {
          const event = pushAgentEvent({
            type: "thinking",
            label: "Reasoning",
            detail,
            detailFormat: "markdown",
            status: "running",
          });
          reasoningEventId = event.id;
        }
        emit("generating", "Designing slides...");
      },
      onAgentEvent: (event) => {
        latestAgentTimeline = reduceAgentEvent(latestAgentTimeline, event);
        scheduleTimelineEmit();
      },
    });
    const finalizeNarration = async (): Promise<void> => {
      if (signal.aborted) return;
      emit("generating", "Finalizing agent activity...");
      try {
        const result = await narratorSink.finalize();
        if (result.timedOut) {
          log("[agentic-deck][NARRATOR] finalization timed out; completing response");
        }
      } catch (error) {
        log(
          `[agentic-deck][NARRATOR] finalization failed: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    };
    queryOpenAIStreaming(
      content,
      seed,
      {
        onThinking: narratorSink.onThinking,
        onAgentEvent: narratorSink.onAgentEvent,
        onToolCall: (tool) => {
          if (tool === "get_slides_info" && slidesBuilt >= total) {
            pushLog("Reviewing layout");
            const event = pushAgentEvent({
              type: "observation",
              label: "Checking final layout",
              toolName: tool,
              status: "running",
            });
            toolEventIdsByName.set(tool, event.id);
            emit("generating", "Reviewing layout...", {
              currentSlide: Math.max(0, total - 1),
              phase: "finalReview",
              finalReview: { current: total, total },
            });
            return;
          }
          pushLog(`Running ${tool}`);
          const event = pushAgentEvent({
            type: "tool",
            label: `Running ${tool}`,
            toolName: tool,
            status: "running",
          });
          toolEventIdsByName.set(tool, event.id);
          emit("generating", `Running ${tool}...`);
        },
        onToolResult: (results) => {
          for (const result of results) {
            const status = classifyToolResult(result);
            const shouldHideFailedApply = result.tool === "apply_edits" && status !== "ok";
            const detail = summarizeToolResult(result);
            if (shouldHideFailedApply) {
              log(`[agentic-deck] apply_edits did not cleanly apply: ${detail ?? "unknown issue"}`);
            }
            const activeId = toolEventIdsByName.get(result.tool);
            if (activeId) {
              const active = agentEvents.find((event) => event.id === activeId);
              if (active) {
                active.status = status;
                active.detail = detail ?? active.detail;
                active.hidden = shouldHideFailedApply;
                active.type =
                  status === "error"
                    ? "error"
                    : status === "warning"
                      ? "warning"
                      : active.type === "observation"
                        ? "observation"
                        : "tool";
              }
              toolEventIdsByName.delete(result.tool);
            } else {
              pushAgentEvent({
                type: status === "error" ? "error" : status === "warning" ? "warning" : "tool",
                label: result.tool,
                toolName: result.tool,
                status,
                detail,
                hidden: shouldHideFailedApply,
              });
            }
          }
          emit("generating", "Processing tool results...");
        },
        onComplete: async (terminalPayload) => {
          await finalizeNarration();
          if (signal.aborted) {
            finish();
            return;
          }
          // Reconcile to the real created-slide count instead of blindly forcing
          // `total`, so an under-build honestly shows e.g. "6 of 8" at completion.
          slidesBuilt = builtSlideIds.size > 0 ? Math.min(builtSlideIds.size, total) : total;
          agentSummary = getAgentTerminalSummary(terminalPayload);
          await runGoldenCaptureStep(goldenArtifactRun, log, "operations.json", async () => {
            const operationsSnapshot = JSON.parse(JSON.stringify(terminalPayload));
            await uploadGoldenArtifactJson(
              goldenArtifactRun!.runId,
              "operations",
              operationsSnapshot
            );
          });
          try {
            await syncTabsWithPresentation("agentic-deck-complete");
          } catch {
            /* non-fatal */
          }
          // Remove the brand template's bootstrapped placeholder slides that
          // survived (e.g. a host-mandatory slide), but ONLY those still UNCHANGED
          // since open. If the agent modified/filled a survivor (e.g. turned it
          // into the first slide) its content fingerprint differs, so we keep it.
          // Guarded so we never empty a deck where nothing new was created.
          if (bootstrapSlideIds.size > 0) {
            try {
              const postSlides = await listSlides({
                forceRefresh: true,
                presentationRef: effectivePresentationRef ?? undefined,
              });
              const createdCount = postSlides.filter(
                (slide) => !bootstrapSlideIds.has(slide.id)
              ).length;
              const leftover = postSlides.filter((slide) => bootstrapSlideIds.has(slide.id));
              if (createdCount > 0 && leftover.length > 0) {
                // Re-extract the survivors so we can compare current content to the
                // open-time snapshot and delete only untouched placeholders.
                const inspected = await extractPresentationContent(log, {
                  forceRefresh: true,
                  presentationRef: effectivePresentationRef ?? undefined,
                  filters: {
                    slides: {
                      ids: leftover.map((slide) => slide.id),
                      notesPage: false,
                      background: false,
                      headersFooters: false,
                      shapes: RUN_OFFICE_EXTRACTOR_TEXT_ONLY_SHAPE_FILTER,
                    },
                  },
                }).catch(() => null);
                const currentFingerprintById = new Map<string, string>();
                for (const slide of inspected?.slides || []) {
                  currentFingerprintById.set(slide.id, slideContentFingerprint(slide));
                }
                const untouched = leftover.filter((slide) => {
                  const openFp = bootstrapFingerprintById.get(slide.id);
                  const currentFp = currentFingerprintById.get(slide.id);
                  if (openFp !== undefined && currentFp !== undefined) {
                    return currentFp === openFp;
                  }
                  // No open baseline (snapshot failed): fall back to "still empty"
                  // (no visible text) so we never delete a slide carrying content.
                  return currentFp !== undefined && currentFp.endsWith("|");
                });
                if (untouched.length > 0) {
                  if (effectivePresentationRef) {
                    await activatePresentationViaIntents({
                      presentationRef: effectivePresentationRef,
                      log,
                    }).catch(() => undefined);
                  }
                  let removed = 0;
                  for (const slide of [...untouched].sort((a, b) => b.index - a.index)) {
                    const ok = await deleteSlideById(slide.id, {
                      presentationRef: effectivePresentationRef ?? undefined,
                    });
                    if (ok) removed++;
                  }
                  log(
                    `[agentic-deck] removed ${removed}/${untouched.length} untouched bootstrap placeholder(s); kept ${
                      leftover.length - untouched.length
                    } agent-filled`
                  );
                }
              }
            } catch (error) {
              log(
                `[agentic-deck] bootstrap cleanup failed (non-fatal): ${
                  error instanceof Error ? error.message : String(error)
                }`
              );
            }
          }
          // A fresh brand deck can have no writable citation-pool slide before
          // the build. Retry once now that created slides exist.
          await mergeGroundingCitationsIntoPool();
          await attachDeckCitations({
            groundingCitations,
            setTabsState,
            citationBindings,
            declaredCitedValues,
            presentationRef: effectivePresentationRef,
            log,
          });
          // "after.pptx" is the BUILD output (pre-polish). The polish phase
          // below captures its own per-slide artifacts plus "after_polish.pptx"
          // (the final delivered deck), so build vs polish deltas stay separable.
          await runGoldenCaptureStep(goldenArtifactRun, log, "after.pptx", async () => {
            const afterDeckBase64 = await exportPresentationAsBase64Compressed();
            await uploadGoldenArtifactPptx(goldenArtifactRun!.runId, "after", afterDeckBase64);
          });
          // Post-build per-slide visual polish (non-fatal).
          try {
            await runPostBuildVisualPolish();
          } catch (error) {
            log(
              `[agentic-deck] polish phase failed (non-fatal): ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          }
          // Final deck after polish (no-op when polish made no changes).
          await runGoldenCaptureStep(goldenArtifactRun, log, "after_polish.pptx", async () => {
            const afterPolishDeckBase64 = await exportPresentationAsBase64Compressed();
            await uploadGoldenArtifactPptx(
              goldenArtifactRun!.runId,
              "after_polish",
              afterPolishDeckBase64
            );
          });
          pushAgentEvent({
            type: "tool",
            label: "Deck created",
            detail: agentSummary ?? "Agent completed the deck build.",
            detailFormat: agentSummary ? "markdown" : "plain",
            status: "ok",
          });
          const finalPolish = lastEmitArgs.opts?.polish;
          emit("complete", "Deck created.", {
            currentSlide: Math.max(0, total - 1),
            ...(finalPolish ? { polish: finalPolish } : {}),
          });
          setAiMode("chat");
          finish();
        },
        onError: (err) => {
          void (async () => {
            try {
              await finalizeNarration();
              const activityError = err;
              log(`[agentic-deck] error: ${err}`);
              pushAgentEvent({
                type: "error",
                label: "Deck build stopped",
                detail: activityError,
                status: "error",
              });
            } catch (handlerError) {
              log(
                `[agentic-deck] failed to handle stream error: ${
                  handlerError instanceof Error
                    ? handlerError.message
                    : String(handlerError)
                }`
              );
            } finally {
              finish(err);
            }
          })();
        },
      },
      undefined,
      undefined,
      toolPolicy,
      {
        signal,
        agentToolContext,
        ...(preludeUserMessages ? { preludeUserMessages } : undefined),
        referenceExamples: effectiveReferenceExamples ?? undefined,
        langfuseContext,
        langfusePhase: "deck_build",
        streamApplyEditSlideSummaries: true,
      }
    ).catch(async (error) => {
      const message = error instanceof Error ? error.message : String(error);
      log(`[agentic-deck] streaming rejected: ${message}`);
      await finalizeNarration();
      finish(message);
    });
  });
}

/**
 * Best-effort: attach SlideCitationsView messages to the slides the build wrote
 * citations onto, keyed on their REAL host ids from the apply pipeline's
 * manifest. Bindings already carry real ids (no createdSlides diff / positional
 * pairing); we only re-key index/number to the final deck order for display.
 * Entirely non-fatal.
 */
async function attachDeckCitations(input: {
  groundingCitations?: readonly Citation[];
  setTabsState?: (updater: (prev: TabsState) => TabsState) => void;
  citationBindings: SlideCitationBinding[];
  declaredCitedValues: Record<string, string>;
  presentationRef: FactSetPresentationRef | null;
  log: (message: string) => void;
}): Promise<void> {
  const {
    groundingCitations,
    setTabsState,
    citationBindings,
    declaredCitedValues,
    presentationRef,
    log,
  } = input;
  if (!setTabsState || !groundingCitations || groundingCitations.length === 0) return;
  if (citationBindings.length === 0) return;

  try {
    // Post-build slide list = the full, final registry (real ids + final
    // index/number). Re-key each binding's index/number to the final deck order
    // (later inserts shift earlier slides) while preserving the real host id.
    const postSlides = await listSlides({
      forceRefresh: true,
      presentationRef: presentationRef ?? undefined,
    });
    const finalRefById = new Map(
      postSlides.map((slide) => [
        slide.id,
        { index: slide.index, slideNumber: slide.slideNumber },
      ])
    );
    const rekeyedBindings = citationBindings.map((binding) => {
      const fin = finalRefById.get(binding.slideId);
      return fin
        ? { ...binding, slideIndex: fin.index, slideNumber: fin.slideNumber ?? binding.slideNumber }
        : binding;
    });

    const messages = buildSlideCitationMessages({
      bindings: rekeyedBindings,
      citations: [...groundingCitations],
      citedValuesByUuid: declaredCitedValues,
      log,
    });
    if (messages.length === 0) return;

    setTabsState((prev) =>
      messages.reduce(
        (state, entry) =>
          attachSyntheticMessageToSlideTab(state, {
            slideId: entry.slide.id,
            slideIndex: entry.slide.index,
            slideNumber: entry.slide.slideNumber,
            message: entry.message,
          }),
        prev
      )
    );
  } catch (error) {
    log(
      `[agentic-deck] citation attach failed (non-fatal): ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}
